import {
  Person,
  Invitation,
  Household,
  Place,
  AddressComponent,
  Address,
  GuestWeddingQuestions,
  Answer,
  Event,
} from '../interfaces';

const emptyPerson = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
};

export const createEmptyPerson = (events: any[], selectedEvent: Event | undefined): Person => {
  let invitations: Invitation[] = [];
  const weddingEvent = events?.find(({ type }) => type === 'Ceremony' || type === 'WeddingDay');

  if (weddingEvent) {
    const addWedding = { eventId: weddingEvent.id };
    invitations = [...invitations, addWedding];
  }
  if (selectedEvent && selectedEvent.id !== weddingEvent?.id) {
    const addSelected = { eventId: selectedEvent.id };
    invitations = [...invitations, addSelected];
  }

  return { ...emptyPerson, isLeader: false, invitations };
};

export const createEmptyLeader = (events: any[], selectedEvent: Event | undefined): Person => {
  const person = createEmptyPerson(events, selectedEvent);
  return { ...person, isLeader: true };
};

export const denormalizeData = (formData: any): Household => {
  // Remove rsvpNote
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rsvpNote,
    invitations: tyGiftInvitations,
    people: formPeople,
    answers: formAnswers,
    ...data
  } = formData;
  const people: Person[] = formPeople.map((person: Person) => {
    const invitations = person.invitations || [];
    const newInvitations = invitations.map(invite => {
      const updatedInvitation = { ...invite };

      // take changes for thankYouSent and gifts
      if (tyGiftInvitations && tyGiftInvitations.length) {
        const foundInvitation = tyGiftInvitations.find((i: { id: string | undefined }) => i.id === invite.id);
        if (foundInvitation) {
          const { thankYouSent, gift } = foundInvitation;
          updatedInvitation.thankYouSent = thankYouSent;
          updatedInvitation.gift = gift;
        }
      }

      // filter out form placeholder answers that do not have content
      if (invite.answers && invite.answers.length) {
        updatedInvitation.answers = invite.answers.filter(a => !!a && !!(a.text || a.optionId));
      }

      return updatedInvitation;
    });
    return { ...person, invitations: newInvitations };
  });

  let answers: Answer[] | null = [...(formAnswers || [])];
  if (answers && answers.length) {
    answers = answers.filter(a => !!a && !!(a.text || a.optionId));
  }

  answers = answers.length ? answers : null;

  return { ...data, people, answers };
};

export const denormalizeBulkData = (bulkFormData: any): Household[] =>
  bulkFormData.map((formData: any) => denormalizeData(formData));

// Google Address Components are given to us in an array, but we need them as an object
export const objectifyAddressComponents = (addressComponents: AddressComponent[]): Place => {
  const place = {} as Place;
  addressComponents.forEach(component => {
    component.types.forEach(componentType => {
      if (componentType === 'administrative_area_level_1') {
        place[componentType] = component.short_name;
      } else {
        // @ts-ignore
        place[componentType] = component.long_name;
      }
    });
  });

  return place;
};

export const getPartyType = (numOfPeople: number): string => {
  let typeOfParty = 'individual';
  if (numOfPeople > 2) typeOfParty = 'household';
  else if (numOfPeople === 2) typeOfParty = 'couple';

  return typeOfParty;
};

// Return whether there are any non-empty values other than
// fulllAddress in the given address
export const doesAddressOnlyIncludeFullAddress = (address: Address | null): boolean => {
  if (!address || address.fullAddress === '' || address.fullAddress === undefined || address.fullAddress === null) {
    return false;
  }

  for (const k in address) {
    const v = address[k as keyof Address];
    if (k !== 'fullAddress' && v !== undefined && v !== '') {
      return false;
    }
  }
  return true;
};

// Ensure that person attributes default to empty string if we receive them as null.
export const removePeopleNulls = (people: Person[]): Person[] => {
  const cleanedPeople = people.map(person => {
    const cleanPerson = person;
    cleanPerson.firstName = person.firstName ? person.firstName : '';
    cleanPerson.lastName = person.lastName ? person.lastName : '';
    cleanPerson.email = person.email ? person.email : '';
    cleanPerson.phone = person.phone ? person.phone : '';

    if (cleanPerson.invitations) {
      // Do the same for invitations.
      const cleanedInvitations = cleanPerson.invitations.map(invitation => {
        const cleanedInvite = invitation;
        cleanedInvite.gift = cleanedInvite.gift ? cleanedInvite.gift : '';
        cleanedInvite.meal = cleanedInvite.meal ? cleanedInvite.meal : '';
        cleanedInvite.thankYouSent = cleanedInvite.thankYouSent ? cleanedInvite.thankYouSent : false;
        cleanedInvite.invitationSent = cleanedInvite.invitationSent ? cleanedInvite.invitationSent : false;
        return cleanedInvite;
      });

      cleanPerson.invitations = cleanedInvitations;
    }

    return cleanPerson;
  });

  return cleanedPeople;
};

export const getRsvpNoteAnswer = (questions: GuestWeddingQuestions, household: Household): string | undefined => {
  let rsvpNote;
  if (questions?.length) {
    const RSVP_NOTE_TYPE = 'RSVPNote';
    const rsvpNoteQuestion = questions.find(q => q.type === RSVP_NOTE_TYPE);
    const rsvpNoteAnswer = household.answers?.find(a => a.questionId === rsvpNoteQuestion?.id);
    rsvpNote = rsvpNoteAnswer?.text;
  }
  return rsvpNote;
};

export const isKeyEnterSpace = ({ keyCode }: { keyCode: number }): boolean => {
  const SPACE_KEY_CODE = 32;
  const ENTER_KEY_CODE = 13;
  return keyCode === SPACE_KEY_CODE || keyCode === ENTER_KEY_CODE;
};
